import { getUserName } from "../../../utils/helpers";

/**
 * The sentence the "action required" modal shows for one follow request.
 *
 * ⛔ This is an app-blanking path (D143.5). Three copies of this logic lived as closures inside
 * FollowersList, FollowingList and PendingRequests, and all three were wrong the same way:
 *
 *   const getDisplayName = (profile: ProfileDetails) => {
 *     const fullName = profile.profile.last_name ? … : profile.profile.first_name?.trim();
 *     return fullName ? fullName : profile.profile.phone ? profile.profile.phone
 *                                                        : profile.profile.email;
 *   };
 *   const getTitle = () => {
 *     const displayName = getDisplayName(selectedProfile);      // ← above the switch
 *     switch (selectedProfile.type) { … }
 *   };
 *
 * Three defects in that shape:
 *  1. `selectedProfile` starts as `null` and is only ever an item, never the `ProfileDetails`
 *     the annotation claimed — so `profile.profile` was a bare deref of two absent objects.
 *  2. The display name was computed ABOVE the switch, so an item with no person attached threw
 *     before `default` — the branch that renders the item's own `message`, and the branch a
 *     B2-4 org item wants to reuse — could ever be reached.
 *  3. It RETURNED `undefined` for a person with no name, phone or email (every field is
 *     nullable), and the caller then did `.trim()` on that.
 *
 * Because App.tsx's ErrorBoundary is the app's ONLY one and renders `null` (TD-165), any of the
 * three unmounts the WHOLE app — a permanent blank screen on web and native.
 *
 * ⛔ Keep this TOTAL: always a string, for any input including `null`.
 *
 * ⚠️ It lives here, exported and pure, rather than as a closure, because `IonModal` renders its
 * children into a web component that never upgrades under jsdom — modal content is unreachable
 * from a render test, so a test that drove the modal would have passed with the bug restored.
 */
export const requestTitle = (
  item?: { type?: string; message?: string | null; profile?: unknown } | null
): string => {
  const message = item?.message ?? "";
  // ⚠️ Guard the profile OBJECT, not the label `getUserName` returns. `getUserName` is total and
  // its own last resort is the literal "Arc Connect User", so testing the LABEL for emptiness
  // would never take the message fallback — a profile-less item would be announced as
  // "Arc Connect User has requested…" instead of rendering its own message.
  const person = item?.profile;
  switch (person ? item?.type : undefined) {
    case "incoming_following_request":
      return `${getUserName(person as never)} has invited you to view their data.`;
    case "incoming_follower_request":
      return `${getUserName(person as never)} has requested to view your data.`;
    default:
      return message;
  }
};
